import { ethers } from 'ethers';
import { 
  MINING_CONTRACT_ADDRESS, 
  MINING_CONTRACT_ABI,
  CORE_TOKEN_ADDRESS,
  CORE_TOKEN_ABI,
  OPBNB_SCAN_URL
} from '../config/miningConstants';

const getProvider = () => {
  if (typeof window.ethereum !== 'undefined') {
    return new ethers.providers.Web3Provider(window.ethereum);
  }
  throw new Error('No wallet detected');
};

const getSigner = () => getProvider().getSigner();

const getMiningContract = (withSigner = false) => {
  const provider = getProvider();
  if (withSigner) {
    return new ethers.Contract(MINING_CONTRACT_ADDRESS, MINING_CONTRACT_ABI, getSigner());
  }
  return new ethers.Contract(MINING_CONTRACT_ADDRESS, MINING_CONTRACT_ABI, provider);
};

const getCoreTokenContract = () => {
  return new ethers.Contract(CORE_TOKEN_ADDRESS, CORE_TOKEN_ABI, getProvider());
};

// ============ READ FUNCTIONS ============

export const getGlobalStats = async () => {
  try {
    const contract = getMiningContract();
    const stats = await contract.getGlobalStats();
    
    console.log('Global Stats Raw:', stats);
    
    // Use index-based access
    // Returns: _totalMiners, _totalEnergyDistributed, _totalTokensMined, _totalTokensWithdrawn, _paused, _currentDay
    return {
      totalMiners: stats[0].toString(),
      totalEnergyDistributed: stats[1].toString(),
      totalTokensMined: stats[2].toString(),
      totalTokensWithdrawn: stats[3].toString(),
      paused: stats[4],
      currentDay: stats[5].toString()
    };
  } catch (error) {
    console.error('Error getting global stats:', error);
    return null;
  }
};

export const getUserStats = async (address) => {
  try {
    const contract = getMiningContract();
    const data = await contract.getUserStats(address);
    
    // Use index-based access for reliability
    // Returns: smartcoreUserId, totalEnergy, availableEnergy, totalMined, 
    // pendingBalance, withdrawableBalance, totalWithdrawn, lastMinedDay, sessionCount, canMineToday
    console.log('User Stats Raw:', data);
    
    return {
      smartcoreUserId: data[0].toString(),
      totalEnergy: data[1].toString(),
      availableEnergy: data[2].toString(),
      totalMined: data[3].toString(),
      pendingBalance: data[4].toString(),
      withdrawableBalance: data[5].toString(),
      totalWithdrawn: data[6].toString(),
      lastMinedDay: data[7].toString(),
      sessionCount: data[8].toString(),
      canMineToday: data[9]
    };
  } catch (error) {
    console.error('Error getting user stats:', error);
    return null;
  }
};

export const getTodaysMiningReward = async (address) => {
  try {
    const contract = getMiningContract();
    const data = await contract.getTodaysMiningReward(address);
    return {
      reward: data.reward.toString(),
      activeSessions: data.activeSessions.toString()
    };
  } catch (error) {
    console.error('Error getting today reward:', error);
    return { reward: '0', activeSessions: '0' };
  }
};

export const checkSyncAvailable = async (address) => {
  try {
    const contract = getMiningContract();
    const data = await contract.checkSyncAvailable(address);
    return {
      hasNewInvestments: data.hasNewInvestments,
      totalNewEnergy: data.totalNewEnergy.toString(),
      newInvestmentPerPackage: data.newInvestmentPerPackage.map(v => v.toString())
    };
  } catch (error) {
    console.error('Error checking sync:', error);
    return { hasNewInvestments: false, totalNewEnergy: '0', newInvestmentPerPackage: [] };
  }
};

export const isSmartcoreUser = async (address) => {
  try {
    const contract = getMiningContract();
    const [isUser, userId] = await contract.isSmartcoreUser(address);
    return { isUser, userId: userId.toString() };
  } catch (error) {
    console.error('Error checking smartcore user:', error);
    return { isUser: false, userId: '0' };
  }
};

export const getUserActiveSessions = async (address) => {
  try {
    const contract = getMiningContract();
    const data = await contract.getUserActiveSessions(address);
    
    const sessions = [];
    for (let i = 0; i < data.sessionIds.length; i++) {
      sessions.push({
        id: data.sessionIds[i].toString(),
        dailyReward: data.dailyRewards[i].toString(),
        daysRemaining: data.daysRemaining[i].toString()
      });
    }
    return sessions;
  } catch (error) {
    console.error('Error getting active sessions:', error);
    return [];
  }
};

export const getCoreTokenSupply = async () => {
  try {
    const contract = getCoreTokenContract();
    const supply = await contract.totalSupply();
    return supply.toString();
  } catch (error) {
    console.error('Error getting token supply:', error);
    return '0';
  }
};

export const getUserCoreBalance = async (address) => {
  try {
    const contract = getCoreTokenContract();
    const balance = await contract.balanceOf(address);
    return balance.toString();
  } catch (error) {
    console.error('Error getting CORE balance:', error);
    return '0';
  }
};

// ============ WRITE FUNCTIONS ============

export const syncFromSmartcore = async () => {
  try {
    const contract = getMiningContract(true);
    const tx = await contract.syncFromSmartcore();
    const receipt = await tx.wait();
    
    const syncedEvent = receipt.events?.find(e => e.event === 'Synced');
    
    return {
      success: true,
      txHash: receipt.transactionHash,
      newEnergy: syncedEvent?.args?.newEnergy?.toString() || '0',
      newSessions: syncedEvent?.args?.newSessions?.toString() || '0'
    };
  } catch (error) {
    console.error('Error syncing:', error);
    return { success: false, error: error.reason || error.message };
  }
};

export const mineTokens = async () => {
  try {
    const contract = getMiningContract(true);
    const tx = await contract.mine();
    const receipt = await tx.wait();
    
    const minedEvent = receipt.events?.find(e => e.event === 'Mined');
    
    return {
      success: true,
      txHash: receipt.transactionHash,
      amount: minedEvent?.args?.amount?.toString() || '0',
      day: minedEvent?.args?.day?.toString() || '0',
      activeSessions: minedEvent?.args?.activeSessions?.toString() || '0'
    };
  } catch (error) {
    console.error('Error mining:', error);
    return { success: false, error: error.reason || error.message };
  }
};

export const claimPending = async () => {
  try {
    const contract = getMiningContract(true);
    const tx = await contract.claimPending();
    const receipt = await tx.wait();
    
    return {
      success: true,
      txHash: receipt.transactionHash
    };
  } catch (error) {
    console.error('Error claiming pending:', error);
    return { success: false, error: error.reason || error.message };
  }
};

export const withdrawTokens = async () => {
  try {
    const contract = getMiningContract(true);
    const tx = await contract.withdraw();
    const receipt = await tx.wait();
    
    const withdrawnEvent = receipt.events?.find(e => e.event === 'Withdrawn');
    
    return {
      success: true,
      txHash: receipt.transactionHash,
      amount: withdrawnEvent?.args?.amount?.toString() || '0'
    };
  } catch (error) {
    console.error('Error withdrawing:', error);
    return { success: false, error: error.reason || error.message };
  }
};

// ============ HELPER FUNCTIONS ============

export const getTxLink = (txHash) => `${OPBNB_SCAN_URL}/tx/${txHash}`;
export const getAddressLink = (address) => `${OPBNB_SCAN_URL}/address/${address}`;

export const formatCoreValue = (weiValue, decimals = 4) => {
  if (!weiValue) return '0';
  const value = parseFloat(weiValue) / 1e18;
  return value.toFixed(decimals);
};

export const parseCoreValue = (coreValue) => {
  return ethers.utils.parseEther(coreValue.toString()).toString();
};
